import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { ChevronDown, RotateCcw } from 'lucide-react';

import { useAppDispatch, useAppSelector } from '../../../hooks';
import { useStore } from '../../../pages/workspace/hooks';
import { MeasurementData, TargetFunction, setMeasurementFilterRange } from '../../../store/reducers/workspace.reducer';
import {
  MEASUREMENT_VISUALIZATION_FIELD_MAP,
  MEASUREMENT_VISUALIZATION_LABELS,
  MeasurementVisualizationType,
} from '../../../types/view';
import { getPhotometricUnit } from '../../../utils/unitSystem.ts';
import {
  GRADIENT_CSS,
  calculateDisplayScale,
  formatTicksUniform,
  generateTicks,
  getVisualizationUnit,
  hasVisualizationValues,
} from './measurementUtils';

const UI_FONT_STACK =
  '-apple-system, BlinkMacSystemFont, "Segoe UI", Inter, Roboto, "Helvetica Neue", Arial, sans-serif';

interface MeasurementScaleProps {
  minValue: number;
  maxValue: number;
  visualizationType: MeasurementVisualizationType;
  visualizationOptions: MeasurementVisualizationType[];
  onSelectVisualization: (type: MeasurementVisualizationType) => void;
}

const formatValue = (value: number): string => {
  const abs = Math.abs(value);
  if (abs >= 100) return value.toFixed(0);
  if (abs >= 10) return value.toFixed(1);
  return value.toFixed(2);
};

const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

const BAR_HEIGHT = 8;
/** Vertical hit area around the bar for hover/click, to make interaction comfortable. */
const BAR_HIT_PADDING = 8;

/**
 * Drag state captured at the moment a handle is grabbed. Lives in a ref
 * (not state) because we only ever read it from the global pointermove
 * handler — never render off it directly. A separate `dragging` flag
 * (handle name | null) is kept in state for the few re-renders we do need
 * (cursor styling, hiding the hover tick while dragging).
 *
 * `otherEdge` is the *raw* value of the non-dragged handle at drag start
 * (`null` = untouched / unbounded), so the move handler can preserve that
 * edge's status while dispatching updates. `otherEdgeClamp` is the numeric
 * coordinate used to clamp the dragged handle against the other one (falls
 * back to the scale edge when the other handle is untouched).
 */
type DragState = {
  handle: 'min' | 'max';
  otherEdge: number | null;
  otherEdgeClamp: number;
};

type FilterRange = { min: number | null; max: number | null };

const MeasurementScaleContent = ({
  minValue,
  maxValue,
  visualizationType,
  visualizationOptions,
  onSelectVisualization,
}: MeasurementScaleProps) => {
  const dispatch = useAppDispatch();
  const filterRange = useAppSelector((state) => state.workspace.measurementFilterRange);
  const targetFunction = MEASUREMENT_VISUALIZATION_FIELD_MAP[visualizationType] as TargetFunction | null;

  const unit = targetFunction ? getPhotometricUnit(targetFunction) || '' : getVisualizationUnit(visualizationType);
  const label = MEASUREMENT_VISUALIZATION_LABELS[visualizationType];
  const numTicks = 8;
  const ticks = generateTicks(minValue, maxValue, numTicks);
  const tickLabels = formatTicksUniform(ticks);

  // Raw edge values — `null` means "the user never moved this handle off the
  // scale edge", so values past it are NOT filtered out. The booleans drive
  // visuals (dim overlay, handle styling) so the UI clearly distinguishes a
  // constraining edge from an untouched one.
  const rawMin = filterRange?.min ?? null;
  const rawMax = filterRange?.max ?? null;
  const minActive = rawMin !== null;
  const maxActive = rawMax !== null;

  // Effective range for positioning the handles + clamping (untouched edges
  // sit at the scale boundary visually).
  const activeMin = rawMin ?? minValue;
  const activeMax = rawMax ?? maxValue;
  const span = maxValue - minValue;

  const toPct = useCallback(
    (value: number): number => (span <= 0 ? 0 : clamp(((value - minValue) / span) * 100, 0, 100)),
    [minValue, span],
  );
  const fromPct = useCallback((pct: number): number => minValue + (clamp(pct, 0, 100) / 100) * span, [minValue, span]);

  const minPct = toPct(activeMin);
  const maxPct = toPct(activeMax);

  const barRef = useRef<HTMLDivElement>(null);
  const [hoverPct, setHoverPct] = useState<number | null>(null);
  const [dragging, setDragging] = useState<'min' | 'max' | null>(null);

  // ── Visualization-type dropdown (≥2 options) ──────────────────────────────
  // The chevron lives in the header next to the title; its menu is rendered
  // through a portal to document.body so its stacking order can clear the
  // bottom toolbar (the scale otherwise traps the menu inside its own
  // stacking context).
  const chevronButtonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [menuPos, setMenuPos] = useState<{ left: number; bottom: number } | null>(null);
  const hasMultipleVisualizations = visualizationOptions.length >= 2;

  // Anchor the portaled menu above the chevron — the scale sits near the
  // bottom of the viewport, so opening upward keeps it on-screen.
  const recomputeMenuPosition = useCallback(() => {
    const btn = chevronButtonRef.current;
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    setMenuPos({ left: rect.left, bottom: window.innerHeight - rect.top + 6 });
  }, []);

  useEffect(() => {
    if (!pickerOpen) return;
    recomputeMenuPosition();
    const onDocPointerDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (chevronButtonRef.current?.contains(target)) return;
      if (menuRef.current?.contains(target)) return;
      setPickerOpen(false);
    };
    window.addEventListener('mousedown', onDocPointerDown);
    window.addEventListener('resize', recomputeMenuPosition);
    window.addEventListener('scroll', recomputeMenuPosition, true);
    return () => {
      window.removeEventListener('mousedown', onDocPointerDown);
      window.removeEventListener('resize', recomputeMenuPosition);
      window.removeEventListener('scroll', recomputeMenuPosition, true);
    };
  }, [pickerOpen, recomputeMenuPosition]);

  // Mutable drag context + a "latest move handler" pointer. We deliberately
  // route the global pointermove through a ref instead of binding a fresh
  // closure each render — otherwise every dispatched filterRange update
  // would invalidate the closure's captured values, which historically led
  // to tearing down and re-adding the window listeners many times per drag.
  // With this indirection the effect attaches/detaches *exactly* once per
  // drag, regardless of how many times the component re-renders in between.
  const dragRef = useRef<DragState | null>(null);
  const onMoveRef = useRef<(e: PointerEvent) => void>(() => {});

  const pctFromClientX = useCallback((clientX: number): number => {
    const rect = barRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0) return 0;
    return clamp(((clientX - rect.left) / rect.width) * 100, 0, 100);
  }, []);

  /**
   * Build the next filter-range object after the given handle resolves to
   * `value`. Snaps the handle to `null` when it reaches its scale boundary
   * (so the edge becomes "untouched" again). Collapses the whole filter to
   * `null` when both edges end up untouched.
   */
  const buildNextRange = (handle: 'min' | 'max', value: number, otherEdge: number | null): FilterRange | null => {
    const epsilon = span * 0.001;
    let handleValue: number | null;
    if (handle === 'min') {
      handleValue = value <= minValue + epsilon ? null : value;
    } else {
      handleValue = value >= maxValue - epsilon ? null : value;
    }

    const next: FilterRange =
      handle === 'min' ? { min: handleValue, max: otherEdge } : { min: otherEdge, max: handleValue };
    return next.min === null && next.max === null ? null : next;
  };

  // Keep the "latest" move handler updated on every render so it always
  // sees the freshest minValue/maxValue/etc. without invalidating the
  // window listener identity.
  onMoveRef.current = (e: PointerEvent) => {
    const drag = dragRef.current;
    if (!drag) return;
    const pct = pctFromClientX(e.clientX);
    const value = fromPct(pct);
    const clamped = drag.handle === 'min' ? Math.min(value, drag.otherEdgeClamp) : Math.max(value, drag.otherEdgeClamp);
    dispatch(setMeasurementFilterRange(buildNextRange(drag.handle, clamped, drag.otherEdge)));
    setHoverPct(pct);
  };

  // Global drag listeners: attached only while a handle is being dragged.
  // Depends solely on the `dragging` boolean, so it runs once at drag start
  // and once at drag end — never in between, even though filterRange (and
  // therefore this component) re-renders on every pointermove.
  useEffect(() => {
    if (!dragging) return;

    const onMove = (e: PointerEvent) => onMoveRef.current(e);
    const endDrag = () => {
      dragRef.current = null;
      setDragging(null);
    };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', endDrag, { capture: true });
    window.addEventListener('pointercancel', endDrag, { capture: true });
    // If the window loses focus mid-drag (alt-tab, devtools, etc.) the
    // pointerup may never arrive — bail out so we don't get stuck.
    window.addEventListener('blur', endDrag);

    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', endDrag, { capture: true } as EventListenerOptions);
      window.removeEventListener('pointercancel', endDrag, { capture: true } as EventListenerOptions);
      window.removeEventListener('blur', endDrag);
    };
  }, [dragging]);

  const startDrag = (handle: 'min' | 'max') => (e: React.PointerEvent) => {
    e.stopPropagation();
    e.preventDefault();
    // Snapshot the non-dragged edge's raw value (preserves null = untouched)
    // alongside the numeric coordinate we'll use for clamping, so the move
    // loop is closure-free and the "untouched" status of the non-dragged
    // edge is preserved across the drag.
    const otherRaw = handle === 'min' ? rawMax : rawMin;
    const otherClamp = handle === 'min' ? (otherRaw ?? maxValue) : (otherRaw ?? minValue);
    dragRef.current = { handle, otherEdge: otherRaw, otherEdgeClamp: otherClamp };
    setDragging(handle);
  };

  /**
   * Shared value-application path for both pointer drag and keyboard input.
   * Constrains the new value against the *other* handle so we never invert
   * the range and dispatches the result through {@link buildNextRange} so
   * an untouched edge stays `null` (unbounded).
   */
  const applyHandleValue = (handle: 'min' | 'max', value: number) => {
    const otherEdge = handle === 'min' ? rawMax : rawMin;
    const otherClamp = handle === 'min' ? (otherEdge ?? maxValue) : (otherEdge ?? minValue);
    const clamped = handle === 'min' ? Math.min(value, otherClamp) : Math.max(value, otherClamp);
    dispatch(setMeasurementFilterRange(buildNextRange(handle, clamped, otherEdge)));
  };

  // Keyboard step sizes (standard ARIA slider convention):
  //   arrow key  -> 1% of full scale
  //   PageUp/Dn  -> 10% of full scale
  // Home / End jump to the per-handle bounds (see Handle below).
  const keyStep = span / 100;
  const keyLargeStep = span / 10;

  const handleBarPointerMove = (e: React.PointerEvent) => {
    setHoverPct(pctFromClientX(e.clientX));
  };
  const handleBarPointerLeave = () => {
    if (!dragging) setHoverPct(null);
  };

  const handleDoubleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    dispatch(setMeasurementFilterRange(null));
  };

  const handleReset = (e: React.MouseEvent) => {
    e.stopPropagation();
    dispatch(setMeasurementFilterRange(null));
  };

  // Hover tooltip uses the dragging-pinned value while dragging so the readout
  // doesn't flicker as the cursor slides off the handle.
  const tooltipPct = hoverPct;
  const tooltipValue = tooltipPct !== null ? fromPct(tooltipPct) : null;

  const isFiltered = filterRange !== null;

  const stopPropagation = (e: React.MouseEvent | React.PointerEvent) => {
    e.stopPropagation();
  };

  return (
    <div
      data-testid="measurement-scale"
      data-max={maxValue}
      style={{
        position: 'absolute',
        bottom: '94px',
        left: '50%',
        transform: 'translateX(-50%)',
        width: 'min(440px, 36vw)',
        background: 'rgba(18, 18, 20, 0.78)',
        backdropFilter: 'blur(18px) saturate(160%)',
        WebkitBackdropFilter: 'blur(18px) saturate(160%)',
        padding: '12px 16px 14px 16px',
        borderRadius: '14px',
        border: '1px solid rgba(255, 255, 255, 0.09)',
        boxShadow: '0 12px 36px rgba(0, 0, 0, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.06)',
        pointerEvents: 'auto',
        zIndex: 50,
        fontFamily: UI_FONT_STACK,
        userSelect: 'none',
      }}
      onClick={stopPropagation}
      onPointerDown={stopPropagation}
      onPointerUp={stopPropagation}
      onMouseDown={stopPropagation}
      onMouseUp={stopPropagation}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '10px',
          gap: '8px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0, flex: 1 }}>
          <span
            style={{
              color: 'rgba(255, 255, 255, 0.95)',
              fontSize: '12px',
              fontWeight: 600,
              letterSpacing: '0.01em',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {label}
          </span>
          {hasMultipleVisualizations && (
            <button
              ref={chevronButtonRef}
              onClick={() => setPickerOpen((v) => !v)}
              title="Change visualization"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '20px',
                height: '20px',
                padding: 0,
                borderRadius: '6px',
                background: pickerOpen ? 'rgba(255, 255, 255, 0.12)' : 'rgba(255, 255, 255, 0.06)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                color: 'rgba(255, 255, 255, 0.8)',
                cursor: 'pointer',
                fontFamily: UI_FONT_STACK,
                flexShrink: 0,
              }}
            >
              <ChevronDown
                size={12}
                style={{ transition: 'transform 120ms ease', transform: pickerOpen ? 'rotate(180deg)' : undefined }}
              />
            </button>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
          {isFiltered && (
            <button
              onClick={handleReset}
              title="Reset filter"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                color: 'rgba(255, 255, 255, 0.8)',
                fontSize: '10px',
                fontWeight: 600,
                letterSpacing: '0.02em',
                padding: '2px 7px 2px 6px',
                borderRadius: '999px',
                background: 'rgba(179, 236, 73, 0.14)',
                border: '1px solid rgba(179, 236, 73, 0.3)',
                cursor: 'pointer',
                fontFamily: UI_FONT_STACK,
                lineHeight: 1,
              }}
            >
              <RotateCcw size={10} />
              <span>reset</span>
            </button>
          )}
          <span
            style={{
              color: 'rgba(255, 255, 255, 0.7)',
              fontSize: '10px',
              fontWeight: 600,
              letterSpacing: '0.02em',
              padding: '2px 7px',
              borderRadius: '999px',
              background: 'rgba(255, 255, 255, 0.06)',
              border: '1px solid rgba(255, 255, 255, 0.08)',
            }}
          >
            {unit}
          </span>
        </div>
      </div>

      {/* Bar wrapper: wider hit area for hover/drag than the visible bar itself. */}
      <div
        style={{
          position: 'relative',
          width: '100%',
          paddingTop: `${BAR_HIT_PADDING}px`,
          paddingBottom: `${BAR_HIT_PADDING}px`,
          cursor: dragging ? 'grabbing' : 'crosshair',
        }}
        onPointerMove={handleBarPointerMove}
        onPointerLeave={handleBarPointerLeave}
        onDoubleClick={handleDoubleClick}
      >
        <div
          ref={barRef}
          style={{
            position: 'relative',
            width: '100%',
            height: `${BAR_HEIGHT}px`,
            background: GRADIENT_CSS,
            borderRadius: '999px',
            boxShadow:
              'inset 0 0 0 1px rgba(255, 255, 255, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.22), 0 1px 6px rgba(0, 0, 0, 0.35)',
          }}
        >
          {/* Dim overlays cover only the *constraining* side(s). An untouched
              edge intentionally leaves its half of the bar fully lit so it's
              obvious values past that edge are NOT being filtered out. */}
          {minActive && minPct > 0 && (
            <div
              style={{
                position: 'absolute',
                left: 0,
                top: 0,
                bottom: 0,
                width: `${minPct}%`,
                background: 'rgba(8, 8, 10, 0.65)',
                borderTopLeftRadius: '999px',
                borderBottomLeftRadius: '999px',
                pointerEvents: 'none',
              }}
            />
          )}
          {maxActive && maxPct < 100 && (
            <div
              style={{
                position: 'absolute',
                right: 0,
                top: 0,
                bottom: 0,
                width: `${100 - maxPct}%`,
                background: 'rgba(8, 8, 10, 0.65)',
                borderTopRightRadius: '999px',
                borderBottomRightRadius: '999px',
                pointerEvents: 'none',
              }}
            />
          )}

          {/* Hover cursor line (suppressed while dragging a handle to keep focus on the handle) */}
          {tooltipPct !== null && !dragging && (
            <div
              style={{
                position: 'absolute',
                left: `${tooltipPct}%`,
                top: '-3px',
                bottom: '-3px',
                width: '1px',
                background: 'rgba(255, 255, 255, 0.7)',
                pointerEvents: 'none',
                transform: 'translateX(-50%)',
              }}
            />
          )}

          {/* Min handle: bounded above by the current max so keyboard / drag
              can never invert the range. `untouched` controls whether the
              handle reads as "not currently constraining" (dimmer) or as an
              active filter edge (solid). */}
          <Handle
            pct={minPct}
            value={activeMin}
            valueMin={minValue}
            valueMax={activeMax}
            step={keyStep}
            largeStep={keyLargeStep}
            unit={unit}
            active={dragging === 'min'}
            untouched={!minActive}
            onPointerDown={startDrag('min')}
            onAdjust={(v) => applyHandleValue('min', v)}
            side="min"
          />
          {/* Max handle: bounded below by the current min. */}
          <Handle
            pct={maxPct}
            value={activeMax}
            valueMin={activeMin}
            valueMax={maxValue}
            step={keyStep}
            largeStep={keyLargeStep}
            unit={unit}
            active={dragging === 'max'}
            untouched={!maxActive}
            onPointerDown={startDrag('max')}
            onAdjust={(v) => applyHandleValue('max', v)}
            side="max"
          />
        </div>

        {/* Hover/drag tooltip */}
        {tooltipPct !== null && tooltipValue !== null && (
          <div
            style={{
              position: 'absolute',
              left: `${tooltipPct}%`,
              bottom: `${BAR_HIT_PADDING + BAR_HEIGHT + 6}px`,
              transform: 'translateX(-50%)',
              padding: '3px 7px',
              borderRadius: '6px',
              background: 'rgba(20, 20, 22, 0.92)',
              border: '1px solid rgba(255, 255, 255, 0.12)',
              boxShadow: '0 4px 10px rgba(0, 0, 0, 0.4)',
              color: 'rgba(255, 255, 255, 0.95)',
              fontSize: '10.5px',
              fontVariantNumeric: 'tabular-nums',
              fontWeight: 600,
              whiteSpace: 'nowrap',
              pointerEvents: 'none',
              letterSpacing: '0.01em',
            }}
          >
            {formatValue(tooltipValue)} <span style={{ opacity: 0.6, fontWeight: 500 }}>{unit}</span>
          </div>
        )}
      </div>

      <div
        style={{
          position: 'relative',
          width: '100%',
          height: '14px',
          marginTop: '4px',
        }}
      >
        {ticks.map((_value, index) => {
          const showLabel = index === 0 || index === numTicks || index % 2 === 0;
          if (!showLabel) return null;

          const isEdge = index === 0 || index === numTicks;
          return (
            <div
              key={index}
              style={{
                position: 'absolute',
                left: `${(index / numTicks) * 100}%`,
                transform:
                  index === 0 ? 'translateX(0)' : index === numTicks ? 'translateX(-100%)' : 'translateX(-50%)',
                color: isEdge ? 'rgba(255, 255, 255, 0.88)' : 'rgba(255, 255, 255, 0.55)',
                fontSize: '10px',
                fontVariantNumeric: 'tabular-nums',
                whiteSpace: 'nowrap',
                fontWeight: isEdge ? 600 : 400,
                letterSpacing: '0.01em',
              }}
            >
              {tickLabels[index]}
            </div>
          );
        })}
      </div>

      {pickerOpen &&
        menuPos &&
        createPortal(
          <div
            ref={menuRef}
            style={{
              position: 'fixed',
              left: menuPos.left,
              bottom: menuPos.bottom,
              minWidth: '220px',
              background: 'rgba(28, 28, 30, 0.98)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '8px',
              boxShadow: '0 10px 30px rgba(0, 0, 0, 0.55)',
              padding: '4px',
              zIndex: 2147483647,
              fontFamily: UI_FONT_STACK,
            }}
          >
            {visualizationOptions.map((type) => {
              const isSelected = type === visualizationType;
              return (
                <button
                  key={type}
                  onClick={() => {
                    onSelectVisualization(type);
                    setPickerOpen(false);
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '8px',
                    width: '100%',
                    padding: '8px 10px',
                    borderRadius: '6px',
                    background: 'transparent',
                    border: 'none',
                    color: isSelected ? '#B3EC49' : 'rgba(255, 255, 255, 0.85)',
                    fontSize: '12px',
                    fontWeight: 500,
                    textAlign: 'left',
                    cursor: 'pointer',
                    fontFamily: UI_FONT_STACK,
                  }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255, 255, 255, 0.06)';
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
                  }}
                >
                  <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {MEASUREMENT_VISUALIZATION_LABELS[type]}
                  </span>
                  {isSelected && <span style={{ flexShrink: 0 }}>✓</span>}
                </button>
              );
            })}
          </div>,
          document.body,
        )}
    </div>
  );
};

interface HandleProps {
  /** Position on the bar (0-100), purely visual. */
  pct: number;
  /** Current value in measurement units — drives aria-valuenow. */
  value: number;
  /** Lower bound this handle can reach (scale min, or the other handle). */
  valueMin: number;
  /** Upper bound this handle can reach (the other handle, or scale max). */
  valueMax: number;
  /** Keyboard arrow-key step (~1% of full scale). */
  step: number;
  /** Keyboard PageUp/PageDown step (~10% of full scale). */
  largeStep: number;
  /** Measurement unit (lx, cd/m²…) — used in aria-valuetext readout. */
  unit: string;
  /** True while a pointer drag is in progress on this handle. */
  active: boolean;
  /**
   * True when this handle still sits at its scale edge and is therefore NOT
   * filtering anything out. Rendered with a muted "ghost" appearance so the
   * user can tell at a glance which edges are actually constraining.
   */
  untouched: boolean;
  side: 'min' | 'max';
  onPointerDown: (e: React.PointerEvent) => void;
  /** Apply a new (already-unclamped) value selected via the keyboard. */
  onAdjust: (value: number) => void;
}

/**
 * Accessible range slider thumb. Implements the WAI-ARIA "slider" pattern:
 *
 * - `tabIndex={0}` so it joins the tab order.
 * - `aria-valuemin` / `aria-valuemax` reflect the dynamic bounds this thumb
 *   can reach without inverting the range against its sibling.
 * - `aria-valuenow` carries the current numeric value; `aria-valuetext`
 *   gives screen readers a unit-aware spoken readout.
 * - Keyboard: Arrow keys = ±step, PageUp/PageDown = ±largeStep,
 *   Home/End = jump to the per-thumb bounds.
 * - Focus is signaled with a tinted ring (drawn via boxShadow so we can keep
 *   the rest of the styling inline).
 */
const Handle = ({
  pct,
  value,
  valueMin,
  valueMax,
  step,
  largeStep,
  unit,
  active,
  untouched,
  side,
  onPointerDown,
  onAdjust,
}: HandleProps) => {
  const [focused, setFocused] = useState(false);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    let next: number;
    switch (e.key) {
      case 'ArrowLeft':
      case 'ArrowDown':
        next = value - step;
        break;
      case 'ArrowRight':
      case 'ArrowUp':
        next = value + step;
        break;
      case 'PageDown':
        next = value - largeStep;
        break;
      case 'PageUp':
        next = value + largeStep;
        break;
      case 'Home':
        next = valueMin;
        break;
      case 'End':
        next = valueMax;
        break;
      default:
        return;
    }
    e.preventDefault();
    e.stopPropagation();
    onAdjust(clamp(next, valueMin, valueMax));
  };

  // Two-axis styling:
  //   active  = pointer is currently dragging this handle (lime ring)
  //   focused = keyboard focus              (slightly stronger lime ring)
  //   untouched = handle is "parked" at its scale edge, NOT filtering — drawn
  //               smaller, hollow, and translucent so it reads as a ghost
  //               anchor rather than an active control.
  const ring = active
    ? '0 0 0 3px rgba(179, 236, 73, 0.45), 0 2px 6px rgba(0, 0, 0, 0.5)'
    : focused
      ? '0 0 0 3px rgba(179, 236, 73, 0.55), 0 2px 6px rgba(0, 0, 0, 0.5)'
      : untouched
        ? '0 2px 4px rgba(0, 0, 0, 0.45)'
        : '0 2px 4px rgba(0, 0, 0, 0.4)';

  const background = untouched
    ? active
      ? 'rgba(255, 255, 255, 0.95)'
      : 'rgba(255, 255, 255, 0.7)'
    : active
      ? '#ffffff'
      : 'rgba(255, 255, 255, 0.94)';

  return (
    <div
      onPointerDown={onPointerDown}
      onKeyDown={handleKeyDown}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      role="slider"
      tabIndex={0}
      aria-label={side === 'min' ? 'Filter minimum' : 'Filter maximum'}
      aria-valuemin={valueMin}
      aria-valuemax={valueMax}
      aria-valuenow={value}
      aria-valuetext={untouched ? `${value.toFixed(2)} ${unit} (no limit)` : `${value.toFixed(2)} ${unit}`}
      aria-orientation="horizontal"
      title={untouched ? `${side === 'min' ? 'No lower' : 'No upper'} limit — drag to set` : undefined}
      style={{
        position: 'absolute',
        left: `${pct}%`,
        top: '50%',
        transform: 'translate(-50%, -50%)',
        width: untouched ? '9px' : '10px',
        height: untouched ? '16px' : '18px',
        borderRadius: '4px',
        background,
        border: untouched ? '1px dashed rgba(40, 40, 40, 0.7)' : '1px solid rgba(0, 0, 0, 0.35)',
        boxShadow: ring,
        cursor: active ? 'grabbing' : 'grab',
        touchAction: 'none',
        transition: 'box-shadow 120ms ease, width 120ms ease, height 120ms ease, background 120ms ease',
        zIndex: 2,
        // Custom focus ring is provided via boxShadow; suppress UA default
        // so we don't get a doubled indicator.
        outline: 'none',
      }}
    >
      {/* Grip lines (purely decorative). Hidden on untouched handles to keep
          the ghost state visually minimal. */}
      {!untouched && (
        <div
          aria-hidden
          style={{
            position: 'absolute',
            inset: '4px 3px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            pointerEvents: 'none',
          }}
        >
          <span style={{ display: 'block', height: '1px', background: 'rgba(0, 0, 0, 0.25)' }} />
          <span style={{ display: 'block', height: '1px', background: 'rgba(0, 0, 0, 0.25)' }} />
          <span style={{ display: 'block', height: '1px', background: 'rgba(0, 0, 0, 0.25)' }} />
        </div>
      )}
    </div>
  );
};

interface MeasurementScaleWrapperProps {
  measurementData: MeasurementData[] | null | undefined;
}

const MeasurementScale = ({ measurementData: modelData }: MeasurementScaleWrapperProps) => {
  const measurementVisualizationType = useAppSelector((state) => state.workspace.measurementVisualizationType)!;
  const { setMeasurementVisualizationType } = useStore();
  const planeCount = useAppSelector((state) => state.measurementPlanes.planes.length);
  const pointsView = useAppSelector((state) => state.measurementPlanes.pointsView);
  const surfaceResults = useAppSelector((state) => state.measurementPlanes.results);
  // Same rule as the workspace page: the model's points show unless the switch is on the
  // calculation areas' points.
  const showingModelPoints = planeCount === 0 || pointsView === 'model';
  // The areas' points share the model's scale; when the model has no values for the chosen
  // quantity (e.g. illuminance on a model with only M-class luminance), use the areas' own.
  const measurementData =
    !showingModelPoints && surfaceResults.length > 0 && !hasVisualizationValues(modelData, measurementVisualizationType)
      ? surfaceResults
      : modelData;

  // Detect which photometric values are present in the measurement data.
  const visualizationOptions = useMemo<MeasurementVisualizationType[]>(() => {
    if (!measurementData) return [];
    let hasL = false;
    let hasE = false;
    let hasEv = false;
    let hasEsc = false;
    let hasEhs = false;
    for (const layer of measurementData) {
      for (const actual of layer.actualValues) {
        if (actual.value == null) continue;
        if (actual.function === 'L') hasL = true;
        else if (actual.function === 'E') hasE = true;
        else if (actual.function === 'Ev') hasEv = true;
        else if (actual.function === 'Esc') hasEsc = true;
        else if (actual.function === 'Ehs') hasEhs = true;
      }
    }
    const options: MeasurementVisualizationType[] = [];
    if (hasL) options.push('luminance');
    if (hasE) options.push('horizontalIlluminance');
    if (hasEv) options.push('verticalIlluminance');
    if (hasEsc) options.push('semiCylindricalIlluminance');
    if (hasEhs) options.push('hemisphericalIlluminance');
    return options;
  }, [measurementData]);

  // Auto-select the first available visualization once data arrives. Lives in
  // the wrapper (not Content) so it still runs while the null-gates below
  // short-circuit rendering — otherwise we'd never set a type and the scale
  // would stay hidden forever.
  useEffect(() => {
    if (visualizationOptions.length === 0) return;
    if (!measurementVisualizationType) {
      setMeasurementVisualizationType(visualizationOptions[0]);
      return;
    }
    // The model's points can only be coloured by a quantity the model has. A calculation area
    // (P norm → illuminance) or another project may have left one it lacks, which hid the scale
    // and left every point grey. (On the areas' points their runner picks the quantity.)
    if (
      showingModelPoints &&
      measurementVisualizationType !== 'tamanScore' &&
      !visualizationOptions.includes(measurementVisualizationType)
    ) {
      setMeasurementVisualizationType(visualizationOptions[0]);
    }
  }, [measurementVisualizationType, visualizationOptions, setMeasurementVisualizationType, showingModelPoints]);

  const scale = useMemo(
    () => calculateDisplayScale(measurementData, measurementVisualizationType),
    [measurementData, measurementVisualizationType],
  );

  if (measurementVisualizationType === 'tamanScore') {
    return null;
  }

  if (!scale) {
    return null;
  }

  return (
    <MeasurementScaleContent
      minValue={scale.min}
      maxValue={scale.max}
      visualizationType={measurementVisualizationType}
      visualizationOptions={visualizationOptions}
      onSelectVisualization={setMeasurementVisualizationType}
    />
  );
};

export { MeasurementScale };
